import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, IconButton, Stack, Typography, Tooltip } from '@mui/material';
import CallEndIcon from '@mui/icons-material/CallEnd';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import StopScreenShareIcon from '@mui/icons-material/StopScreenShare';
import axios from 'axios';
import ApiLink from 'API';
import { useChat } from 'contexts/ChatContext';

const TALK_RING = '0 0 0 3px #10b981, 0 0 14px rgba(16,185,129,0.6)';

// Attach a voice-activity detector to a stream using a SHARED AudioContext.
// Returns a cleanup function (cancels rAF + disconnects the source node).
// Reusing one AudioContext per call avoids Chromium's ~6-context limit and the
// renderer crash (STATUS_BREAKPOINT) caused by churning many contexts on teardown.
function attachVAD(ctx, stream, cb) {
    if (!ctx || !stream || stream.getAudioTracks().length === 0) return () => {};
    let source = null;
    let analyser = null;
    let rafId = 0;
    let stopped = false;
    try {
        source = ctx.createMediaStreamSource(stream);
        analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        const data = new Float32Array(analyser.frequencyBinCount);
        let silence = 0;
        const tick = () => {
            if (stopped || ctx.state === 'closed') return;
            try {
                analyser.getFloatTimeDomainData(data);
                let sum = 0;
                for (const a of data) sum += a * a;
                const rms = Math.sqrt(sum / data.length);
                if (rms > 0.03) { cb(true); silence = 0; }
                else { silence += 1; if (silence > 20) cb(false); }
            } catch (e) { /* analyser gone */ }
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
    } catch (e) {
        return () => {};
    }
    return () => {
        stopped = true;
        if (rafId) cancelAnimationFrame(rafId);
        try { source && source.disconnect(); } catch (e) { /* ignore */ }
    };
}

const STUN = {
    iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' },
    ],
    bundlePolicy: 'max-bundle',
    iceCandidatePoolSize: 10,
};

const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
const canScreenShare = !isMobile && navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function';

// Remote tile — binds a MediaStream to a video element + name + talking ring.
// Tracks the inbound video track's mute state so a stopped screen-share/camera
// clears to the avatar instead of freezing on the last frame.
function RemoteTile({ stream, name, talking, videoOff }) {
    const ref = useRef(null);
    const [, force] = useState(0);
    useEffect(() => {
        if (ref.current && stream) ref.current.srcObject = stream;
        const vt = stream && stream.getVideoTracks()[0];
        if (!vt) return undefined;
        const rerender = () => force((n) => n + 1);
        vt.addEventListener('mute', rerender);
        vt.addEventListener('unmute', rerender);
        vt.addEventListener('ended', rerender);
        return () => {
            vt.removeEventListener('mute', rerender);
            vt.removeEventListener('unmute', rerender);
            vt.removeEventListener('ended', rerender);
        };
    }, [stream]);
    const vt = stream && stream.getVideoTracks()[0];
    // peer explicitly signalled video off → avatar; otherwise trust track liveness
    const hasVideo = !videoOff && !!vt && vt.readyState === 'live' && !vt.muted && vt.enabled;
    return (
        <Box sx={{
            position: 'relative', bgcolor: '#111', borderRadius: 2, overflow: 'hidden', aspectRatio: '4/3',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: talking ? TALK_RING : 'none', transition: 'box-shadow 0.15s',
        }}>
            <video ref={ref} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', display: hasVideo ? 'block' : 'none' }} />
            {!hasVideo && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: '#aaa' }}>
                    <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 24 }}>
                        {(name || '?').charAt(0).toUpperCase()}
                    </Box>
                    <MicIcon sx={{ fontSize: 20 }} />
                </Box>
            )}
            {/* name label */}
            <Box sx={{ position: 'absolute', left: 8, bottom: 8, px: 1, py: 0.25, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {talking && <MicIcon sx={{ fontSize: 14, color: '#10b981' }} />}
                <Typography variant="caption" sx={{ color: '#fff' }}>{name || 'Member'}</Typography>
            </Box>
        </Box>
    );
}

export default function GroupCall({ roomId, kind = 'video', onEnd }) {
    const { emit, on, myId, authHeaders } = useChat();
    const [remoteStreams, setRemoteStreams] = useState({}); // userId -> MediaStream
    const [micOn, setMicOn] = useState(true);
    const [camOn, setCamOn] = useState(kind === 'video');
    const [sharing, setSharing] = useState(false);
    const [status, setStatus] = useState('connecting');
    const [names, setNames] = useState({});      // userId -> fullname
    const [talking, setTalking] = useState({});  // 'me' | peerId -> bool
    const [peerVideoOff, setPeerVideoOff] = useState({}); // peerId -> true when they turned video off
    const [localReady, setLocalReady] = useState(false);
    const audioCtxRef = useRef(null);             // single shared AudioContext for all VAD
    const vadCleanups = useRef({});               // key('me'|peerId) -> cleanup fn

    const myName = names[myId] || 'You';

    // Load staff directory once for name labels (works for global room too)
    useEffect(() => {
        axios.get(`${ApiLink}/api/chat/users`, authHeaders())
            .then((r) => {
                const map = {};
                (r.data || []).forEach((u) => { map[u._id] = u.fullname || u.username; });
                setNames(map);
            })
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const setTalkingFor = useCallback((key, val) => {
        setTalking((prev) => (prev[key] === val ? prev : { ...prev, [key]: val }));
    }, []);

    const localVideoRef = useRef(null);
    const localStreamRef = useRef(null);
    const camTrackRef = useRef(null); // keep camera track when screen sharing
    const pcs = useRef(new Map()); // userId -> RTCPeerConnection
    const iceQueue = useRef(new Map()); // userId -> [candidate]
    const screenStreamRef = useRef(null); // active getDisplayMedia stream while sharing
    const videoSenders = useRef(new Map()); // peerId -> the (persistent sendrecv) video RTCRtpSender

    const addRemoteTrack = useCallback((userId, track) => {
        setRemoteStreams((prev) => {
            const existing = prev[userId] || new MediaStream();
            if (!existing.getTracks().find((t) => t.id === track.id)) existing.addTrack(track);
            return { ...prev, [userId]: new MediaStream(existing.getTracks()) };
        });
    }, []);

    // After negotiation, find the video transceiver, force it sendrecv (so WE can
    // send too — critical for the answerer/joiner), store its sender, and push the
    // current screen/camera onto it. Works for both offerer and answerer.
    const wireVideoSender = useCallback((peerId, pc) => {
        const vt = pc.getTransceivers().find(
            (t) => (t.sender && t.sender.track && t.sender.track.kind === 'video')
                || (t.receiver && t.receiver.track && t.receiver.track.kind === 'video')
                || (t.mid != null && t.sender && !t.sender.track && !videoSenders.current.has(peerId)),
        );
        const sender = vt ? vt.sender : (pc.getSenders().find((s) => s.track && s.track.kind === 'video') || null);
        if (!sender) return;
        try { if (vt && vt.direction !== 'sendrecv') vt.direction = 'sendrecv'; } catch (e) { /* ignore */ }
        videoSenders.current.set(peerId, sender);
        if (screenStreamRef.current) {
            const st = screenStreamRef.current.getVideoTracks()[0];
            if (st) sender.replaceTrack(st).catch(() => {});
        }
    }, []);

    const createPC = useCallback((peerId, isOfferer) => {
        if (pcs.current.has(peerId)) return pcs.current.get(peerId);
        const pc = new RTCPeerConnection(STUN);
        pcs.current.set(peerId, pc);

        let camSender = null;
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach((t) => {
                const s = pc.addTrack(t, localStreamRef.current);
                if (t.kind === 'video') camSender = s;
            });
        }
        // OFFERER must declare a sendrecv video m-line up front so a video sender
        // exists in the offer. The ANSWERER must NOT pre-add a transceiver — it gets
        // its video transceiver from the offer (wired in wireVideoSender after SRD),
        // otherwise the m-lines misalign and the answerer can't send.
        if (isOfferer && !camSender) {
            const tr = pc.addTransceiver('video', { direction: 'sendrecv' });
            camSender = tr.sender;
        }
        if (camSender) {
            videoSenders.current.set(peerId, camSender);
            if (screenStreamRef.current) {
                const st = screenStreamRef.current.getVideoTracks()[0];
                if (st) camSender.replaceTrack(st).catch(() => {});
            }
        }
        pc.onicecandidate = (e) => {
            if (e.candidate) emit('chat:webrtc_ice', { roomId, toUserId: peerId, candidate: e.candidate });
        };
        pc.ontrack = (e) => {
            addRemoteTrack(peerId, e.track);
            setStatus('active');
        };
        pc.onconnectionstatechange = () => {
            if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) {
                // peer-level cleanup handled on peer_left
            }
        };
        return pc;
    }, [emit, roomId, addRemoteTrack]);

    const flushIce = useCallback(async (peerId, pc) => {
        const q = iceQueue.current.get(peerId);
        if (!q) return;
        while (q.length) {
            try { await pc.addIceCandidate(q.shift()); } catch (e) { /* ignore */ }
        }
    }, []);

    const closePeer = useCallback((peerId) => {
        const pc = pcs.current.get(peerId);
        if (pc) { try { pc.close(); } catch (e) { /* ignore */ } pcs.current.delete(peerId); }
        videoSenders.current.delete(peerId);
        setRemoteStreams((prev) => { const n = { ...prev }; delete n[peerId]; return n; });
        setPeerVideoOff((prev) => { const n = { ...prev }; delete n[peerId]; return n; });
    }, []);

    // ── init: media → join → signaling ─────────────────────────────────────
    useEffect(() => {
        let mounted = true;

        const getMedia = async () => {
            const constraints = { audio: true, video: kind === 'video' };
            try {
                return await navigator.mediaDevices.getUserMedia(constraints);
            } catch (e) {
                try { return await navigator.mediaDevices.getUserMedia({ audio: true, video: false }); }
                catch (e2) { return null; }
            }
        };

        (async () => {
            const stream = await getMedia();
            if (!mounted) { stream && stream.getTracks().forEach((t) => t.stop()); return; }
            localStreamRef.current = stream;
            if (stream) {
                camTrackRef.current = stream.getVideoTracks()[0] || null;
                if (kind === 'voice' && camTrackRef.current) camTrackRef.current.enabled = false;
                if (localVideoRef.current) localVideoRef.current.srcObject = stream;
                // camera is "on" only if we actually have a live video track for a video call
                setCamOn(kind === 'video' && !!camTrackRef.current);
                setMicOn(stream.getAudioTracks().length > 0 && stream.getAudioTracks()[0].enabled);
                setLocalReady(true); // enable local VAD
            } else {
                setCamOn(false);
                setMicOn(false);
            }
            // tell server we're in → get existing peers (they will offer to us)
            emit('chat:call_join', { roomId });
        })();

        // existing peers list (we wait for their offers)
        const offPeers = on('chat:call_peers', ({ peers }) => {
            // nothing to do — existing peers create offers to us (avoids glare)
            if (peers && peers.length === 0) setStatus('waiting');
        });

        // a new participant joined → we (existing) create an offer to them
        const offJoined = on('chat:peer_joined', async ({ userId }) => {
            if (userId === myId) return;
            const pc = createPC(userId, true); // we are the offerer
            try {
                const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
                await pc.setLocalDescription(offer);
                emit('chat:webrtc_offer', { roomId, toUserId: userId, sdp: pc.localDescription });
            } catch (e) { /* ignore */ }
        });

        const offOffer = on('chat:webrtc_offer', async ({ fromUserId, sdp }) => {
            const pc = createPC(fromUserId, false); // we are the answerer
            try {
                await pc.setRemoteDescription(new RTCSessionDescription(sdp));
                wireVideoSender(fromUserId, pc); // adopt the offer's video transceiver as our sender (sendrecv)
                await flushIce(fromUserId, pc);
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                emit('chat:webrtc_answer', { roomId, toUserId: fromUserId, sdp: pc.localDescription });
                setStatus('active');
            } catch (e) { /* ignore */ }
        });

        const offAnswer = on('chat:webrtc_answer', async ({ fromUserId, sdp }) => {
            const pc = pcs.current.get(fromUserId);
            if (!pc) return;
            try { await pc.setRemoteDescription(new RTCSessionDescription(sdp)); await flushIce(fromUserId, pc); } catch (e) { /* ignore */ }
        });

        const offIce = on('chat:webrtc_ice', async ({ fromUserId, candidate }) => {
            const pc = pcs.current.get(fromUserId);
            if (pc && pc.remoteDescription) {
                try { await pc.addIceCandidate(candidate); } catch (e) { /* ignore */ }
            } else {
                if (!iceQueue.current.has(fromUserId)) iceQueue.current.set(fromUserId, []);
                iceQueue.current.get(fromUserId).push(candidate);
            }
        });

        const offLeft = on('chat:peer_left', ({ userId }) => closePeer(userId));

        const offVideo = on('chat:peer_video', ({ userId, on: vOn }) => {
            setPeerVideoOff((prev) => ({ ...prev, [userId]: !vOn }));
        });

        return () => {
            mounted = false;
            offPeers && offPeers();
            offJoined && offJoined();
            offOffer && offOffer();
            offAnswer && offAnswer();
            offIce && offIce();
            offLeft && offLeft();
            offVideo && offVideo();
            pcs.current.forEach((pc) => { try { pc.close(); } catch (e) { /* ignore */ } });
            pcs.current.clear();
            if (localStreamRef.current) localStreamRef.current.getTracks().forEach((t) => t.stop());
            emit('chat:call_leave', { roomId });
        };
    }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Voice-activity detection → green "talking" ring (Google Meet style) ──
    useEffect(() => {
        if (!audioCtxRef.current) {
            try {
                const AC = window.AudioContext || window.webkitAudioContext;
                audioCtxRef.current = new AC();
            } catch (e) { return undefined; }
        }
        const ctx = audioCtxRef.current;
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        const cleanups = vadCleanups.current;

        // local
        if (localReady && localStreamRef.current && !cleanups.me) {
            cleanups.me = attachVAD(ctx, localStreamRef.current, (v) => setTalkingFor('me', v));
        }
        // remotes
        Object.entries(remoteStreams).forEach(([uid, stream]) => {
            if (!cleanups[uid]) cleanups[uid] = attachVAD(ctx, stream, (v) => setTalkingFor(uid, v));
        });
        // detach detectors for peers who left
        Object.keys(cleanups).forEach((key) => {
            if (key !== 'me' && !remoteStreams[key]) {
                try { cleanups[key] && cleanups[key](); } catch (e) { /* ignore */ }
                delete cleanups[key];
                setTalkingFor(key, false);
            }
        });
        return undefined;
    }, [remoteStreams, localReady, setTalkingFor]);

    // detach all detectors + close the shared context once on unmount
    useEffect(() => () => {
        Object.values(vadCleanups.current).forEach((fn) => { try { fn && fn(); } catch (e) { /* ignore */ } });
        vadCleanups.current = {};
        try { audioCtxRef.current && audioCtxRef.current.close(); } catch (e) { /* ignore */ }
        audioCtxRef.current = null;
    }, []);

    // Swap the outbound video track on every peer's persistent video sender.
    // replaceTrack(null) stops sending (receiver's track fires `mute` → shows avatar).
    const setOutboundVideo = useCallback(async (track) => {
        for (const sender of videoSenders.current.values()) {
            try { await sender.replaceTrack(track); } catch (e) { /* ignore */ }
        }
        // announce video state so peers clear/show our tile reliably
        emit('chat:peer_video', { roomId, on: !!track });
    }, [emit, roomId]);

    const toggleMic = async () => {
        let t = localStreamRef.current?.getAudioTracks?.()[0];
        if (t && t.readyState === 'live') { t.enabled = !t.enabled; setMicOn(t.enabled); return; }
        try {
            const m = await navigator.mediaDevices.getUserMedia({ audio: true });
            t = m.getAudioTracks()[0];
            if (!localStreamRef.current) localStreamRef.current = new MediaStream();
            localStreamRef.current.addTrack(t);
            for (const pc of pcs.current.values()) {
                const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'audio');
                if (sender) await sender.replaceTrack(t).catch(() => {});
            }
            setMicOn(true);
        } catch (e) { /* denied */ }
    };

    const toggleCam = async () => {
        if (sharing) return; // not while screen sharing
        let t = localStreamRef.current?.getVideoTracks?.()[0];
        if (t && t.readyState === 'live') {
            // toggle existing camera track: enabled=false → receiver sees avatar
            t.enabled = !t.enabled;
            await setOutboundVideo(t.enabled ? t : null);
            setCamOn(t.enabled);
            if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
            return;
        }
        // no live camera track → acquire it now (audio-only start, or after share/cam off)
        try {
            const cam = await navigator.mediaDevices.getUserMedia({ video: true });
            const track = cam.getVideoTracks()[0];
            if (!localStreamRef.current) localStreamRef.current = new MediaStream();
            localStreamRef.current.getVideoTracks().forEach((old) => localStreamRef.current.removeTrack(old));
            localStreamRef.current.addTrack(track);
            camTrackRef.current = track;
            await setOutboundVideo(track); // replaceTrack on persistent sendrecv sender → no renegotiation
            if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
            setCamOn(true);
        } catch (e) { /* no camera / denied */ }
    };

    const startScreenShare = async () => {
        try {
            const display = await navigator.mediaDevices.getDisplayMedia({ video: true });
            const screenTrack = display.getVideoTracks()[0];
            screenStreamRef.current = display;
            await setOutboundVideo(screenTrack); // replaceTrack everywhere — no renegotiation
            if (localVideoRef.current) localVideoRef.current.srcObject = display;
            setSharing(true);
            setCamOn(true);
            screenTrack.onended = () => stopScreenShare();
        } catch (e) { /* user cancelled */ }
    };

    const stopScreenShare = async () => {
        if (screenStreamRef.current) {
            screenStreamRef.current.getTracks().forEach((t) => t.stop());
            screenStreamRef.current = null;
        }
        const cam = camTrackRef.current;
        // restore camera if we have a live one, else stop sending video (avatar)
        const restore = cam && cam.readyState === 'live' && cam.enabled ? cam : null;
        await setOutboundVideo(restore);
        if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
        setSharing(false);
        setCamOn(!!restore);
    };

    const remoteIds = Object.keys(remoteStreams);
    const cols = remoteIds.length <= 1 ? 1 : 2;

    return (
        <Box sx={{ position: 'fixed', inset: 0, bgcolor: '#000', zIndex: 13000, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                {remoteIds.length === 0 && (
                    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: '#fff' }}>
                        <Typography variant="h4" color="inherit">{status === 'waiting' ? 'ລໍຖ້າຄົນອື່ນເຂົ້າຮ່ວມ…' : 'ກຳລັງເຊື່ອມຕໍ່…'}</Typography>
                    </Stack>
                )}
                <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 1.5 }}>
                    {remoteIds.map((uid) => (
                        <RemoteTile key={uid} stream={remoteStreams[uid]} name={names[uid]} talking={!!talking[uid]} videoOff={!!peerVideoOff[uid]} />
                    ))}
                </Box>
            </Box>

            {/* Local PiP */}
            <Box sx={{
                position: 'absolute', top: 16, right: 16, width: 120, height: 160, bgcolor: '#222',
                borderRadius: 2, overflow: 'hidden', border: '2px solid rgba(255,255,255,0.2)',
                boxShadow: talking.me ? TALK_RING : 'none', transition: 'box-shadow 0.15s',
            }}>
                <video ref={localVideoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)', display: camOn || sharing ? 'block' : 'none' }} />
                {!camOn && !sharing && (
                    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: '#888' }}><VideocamOffIcon /></Stack>
                )}
                {/* local name label */}
                <Box sx={{ position: 'absolute', left: 4, bottom: 4, px: 0.75, py: 0.1, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    {talking.me && <MicIcon sx={{ fontSize: 12, color: '#10b981' }} />}
                    <Typography variant="caption" sx={{ color: '#fff', fontSize: 10 }}>{myName} (You)</Typography>
                </Box>
            </Box>

            {/* Controls */}
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ py: 3, bgcolor: 'rgba(255,255,255,0.05)' }}>
                <Tooltip title={micOn ? 'Mute' : 'Unmute'}>
                    <IconButton onClick={toggleMic} sx={{ bgcolor: micOn ? 'rgba(255,255,255,0.15)' : '#ef4444', color: '#fff', '&:hover': { bgcolor: micOn ? 'rgba(255,255,255,0.25)' : '#dc2626' } }}>
                        {micOn ? <MicIcon /> : <MicOffIcon />}
                    </IconButton>
                </Tooltip>
                {kind === 'video' && (
                    <Tooltip title={camOn ? 'Camera off' : 'Camera on'}>
                        <IconButton onClick={toggleCam} sx={{ bgcolor: camOn ? 'rgba(255,255,255,0.15)' : '#ef4444', color: '#fff' }}>
                            {camOn ? <VideocamIcon /> : <VideocamOffIcon />}
                        </IconButton>
                    </Tooltip>
                )}
                {canScreenShare && (
                    <Tooltip title={sharing ? 'Stop sharing' : 'Share screen'}>
                        <IconButton onClick={sharing ? stopScreenShare : startScreenShare} sx={{ bgcolor: sharing ? '#10b981' : 'rgba(255,255,255,0.15)', color: '#fff' }}>
                            {sharing ? <StopScreenShareIcon /> : <ScreenShareIcon />}
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title="End call">
                    <IconButton onClick={() => onEnd && onEnd()} sx={{ bgcolor: '#ef4444', color: '#fff', width: 56, height: 56, '&:hover': { bgcolor: '#dc2626' } }}>
                        <CallEndIcon />
                    </IconButton>
                </Tooltip>
            </Stack>
        </Box>
    );
}
